import * as React from 'react';

import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { venueDisconnectVenueApi } from '../../../api/venue/venueDisconnectVenueApi';
import { venueListVenuesApi } from '../../../api/venue/venueListVenuesApi';
import { Venue } from '../../../graphql/API';
import { useAppSelector } from '../../../redux/hooks';
import { switchCurrentVenue } from '../../../utils/current-venue';
import AddProfileDialog from '../../Dialogs/AddProfile/AddProfileDialog';
import ActionConfirmationDialog from '../../Dialogs/ActionConfirmation/ActionConfirmationDialog';

// Profile switching lives inside the header user menu: this hook returns the
// menu items to render at the top of that menu plus the dialogs they open.
export const useProfileSwitcher = (onMenuClose: () => void) => {
  const venueState = useAppSelector((state) => state.venue.value);
  const masterVenue = useAppSelector((state) => state.venue.masterVenue);
  const queryClient = useQueryClient();

  const [showAddProfileDialog, setShowAddProfileDialog] = React.useState(false);
  const [disconnectTarget, setDisconnectTarget] = React.useState<Venue | null>(null);
  const [isDisconnecting, setIsDisconnecting] = React.useState(false);

  const masterVenueID = masterVenue?.id;

  const { data: linkedVenues } = useQuery({
    queryKey: ['linked-venues', masterVenueID],
    queryFn: () => venueListVenuesApi(String(masterVenueID)),
    enabled: Boolean(masterVenueID),
  });

  const profiles = React.useMemo(() => {
    if (!masterVenue) return [];
    const subvenues = (linkedVenues || []).filter((x) => x && x.id !== masterVenue.id);
    return [masterVenue, ...subvenues];
  }, [masterVenue, linkedVenues]);

  const handleSelectProfile = React.useCallback(
    (venue: Venue) => {
      if (!masterVenueID) return;
      if (venue.id === venueState?.id) {
        onMenuClose();
        return;
      }
      switchCurrentVenue(venue.id, masterVenueID);
    },
    [masterVenueID, venueState?.id, onMenuClose],
  );

  const handleOpenAddProfile = React.useCallback(() => {
    onMenuClose();
    setShowAddProfileDialog(true);
  }, [onMenuClose]);

  const handleDisconnect = React.useCallback(async () => {
    if (!disconnectTarget || !masterVenueID) return;

    try {
      setIsDisconnecting(true);
      await venueDisconnectVenueApi(disconnectTarget.id);

      if (disconnectTarget.id === venueState?.id) {
        // The active profile was disconnected: go back to the main profile
        switchCurrentVenue(masterVenueID, masterVenueID);
        return;
      }

      enqueueSnackbar({
        message: `You have disconnected ${disconnectTarget.name}`,
        variant: 'success',
      });
      setDisconnectTarget(null);
      queryClient.invalidateQueries({ queryKey: ['linked-venues', masterVenueID] });
    } catch (error) {
      const _err = error as Error;
      enqueueSnackbar({ message: _err.message, variant: 'error' });
      console.error(error);
    } finally {
      setIsDisconnecting(false);
    }
  }, [disconnectTarget, masterVenueID, venueState?.id, queryClient]);

  const profileMenuItems = React.useMemo(() => {
    if (!venueState || !masterVenue) return [];

    return [
      ...profiles.map((profile) => {
        const isActive = profile.id === venueState.id;
        const isMaster = profile.id === masterVenue.id;

        return (
          <MenuItem
            key={profile.id}
            onClick={() => handleSelectProfile(profile)}
            sx={{ py: 1, pr: 1.5, minWidth: 260 }}
          >
            <ListItemIcon>
              <Avatar
                alt={profile.name}
                src={profile.picture?.thumbnail || ''}
                sx={{ width: 32, height: 32 }}
              />
            </ListItemIcon>
            <Box flexGrow={1} overflow='hidden' ml={1}>
              <Typography fontSize='14px' fontWeight={isActive ? 600 : 400} noWrap>
                {profile.name}
              </Typography>
              {isMaster && (
                <Typography fontSize='12px' color='text.secondary'>
                  Main profile
                </Typography>
              )}
            </Box>
            {isActive && <CheckIcon sx={{ fontSize: '18px', ml: 1 }} color='primary' />}
            {!isMaster && (
              <Tooltip title='Disconnect profile'>
                <IconButton
                  size='small'
                  sx={{ ml: 1 }}
                  onClick={(event) => {
                    event.stopPropagation();
                    onMenuClose();
                    setDisconnectTarget(profile);
                  }}
                >
                  <LinkOffIcon sx={{ fontSize: '18px' }} />
                </IconButton>
              </Tooltip>
            )}
          </MenuItem>
        );
      }),
      <MenuItem key='add-profile' onClick={handleOpenAddProfile} sx={{ py: 1 }}>
        <ListItemIcon>
          <Avatar sx={{ width: 32, height: 32, bgcolor: '#f3f4f6' }}>
            <AddIcon sx={{ color: '#1A1C1E', fontSize: '20px' }} />
          </Avatar>
        </ListItemIcon>
        <Typography fontSize='14px' fontWeight={500} ml={1}>
          Add profile
        </Typography>
      </MenuItem>,
      <Divider key='profiles-divider' />,
    ];
  }, [venueState, masterVenue, profiles, handleSelectProfile, handleOpenAddProfile, onMenuClose]);

  const profileDialogs = (
    <>
      {masterVenue && (
        <AddProfileDialog
          masterVenueID={masterVenue.id}
          open={showAddProfileDialog}
          onClose={() => setShowAddProfileDialog(false)}
        />
      )}
      <ActionConfirmationDialog
        open={Boolean(disconnectTarget)}
        title='Disconnect profile'
        description={`Are you sure you want to disconnect ${disconnectTarget?.name || ''}?`}
        confirmButtonText='Disconnect'
        isConfirmLoading={isDisconnecting}
        onConfirm={handleDisconnect}
        onCancel={() => setDisconnectTarget(null)}
        onClose={() => setDisconnectTarget(null)}
      />
    </>
  );

  return { profileMenuItems, profileDialogs };
};
